import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'privacy' | 'terms';
type Section = {
  heading: string;
  body: string;
};
const content: Record<LegalDoc, {
  title: string;
  sections: Section[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que recopilamos',
      body: 'Recopilamos la información que nos proporcionas voluntariamente al agendar una consulta o contactarnos, como tu nombre, correo electrónico y número de teléfono. También podemos recopilar datos de uso del sitio (como dirección IP y páginas visitadas) mediante tecnologías estándar.'
    }, {
      heading: '2. Cómo usamos tu información',
      body: 'Utilizamos tu información para programar consultas, responder a tus solicitudes, brindarte información sobre productos de protección y ahorro, y mejorar nuestros servicios. No vendemos tu información personal a terceros.'
    }, {
      heading: '3. Compartir información',
      body: 'Podemos compartir tu información con agentes licenciados y compañías aseguradoras asociadas únicamente con el fin de brindarte los servicios solicitados, siempre bajo acuerdos de confidencialidad.'
    }, {
      heading: '4. Seguridad de los datos',
      body: 'Implementamos medidas razonables para proteger tu información personal. Sin embargo, ningún método de transmisión por internet es 100% seguro.'
    }, {
      heading: '5. Tus derechos',
      body: 'Puedes solicitar acceso, corrección o eliminación de tu información personal en cualquier momento escribiéndonos a info@protectionandsavings.com.'
    }, {
      heading: '6. Contacto',
      body: 'Si tienes preguntas sobre esta Política de Privacidad, contáctanos en info@protectionandsavings.com.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los términos',
      body: 'Al acceder y utilizar protectionandsavings.com, aceptas estos Términos y Condiciones en su totalidad. Si no estás de acuerdo, por favor no utilices el sitio.'
    }, {
      heading: '2. Naturaleza informativa',
      body: 'El contenido de este sitio tiene fines educativos e informativos y no constituye asesoramiento financiero, legal o fiscal. Toda decisión debe tomarse tras consultar con un agente licenciado o asesor profesional.'
    }, {
      heading: '3. Servicios',
      body: 'Ofrecemos orientación sobre estrategias de protección y ahorro, incluyendo pólizas de Seguro de Vida Universal Indexado (IUL). La emisión de pólizas está sujeta a la aprobación y términos de las compañías aseguradoras emisoras.'
    }, {
      heading: '4. Propiedad intelectual',
      body: 'Todo el contenido, marcas y materiales de este sitio son propiedad de Protection & Savings o de sus respectivos titulares y no pueden reproducirse sin autorización.'
    }, {
      heading: '5. Limitación de responsabilidad',
      body: 'No nos hacemos responsables por decisiones tomadas con base en la información del sitio. Los resultados pueden variar según la edad, salud y aportaciones del asegurado.'
    }, {
      heading: '6. Cambios en los términos',
      body: 'Podemos actualizar estos términos en cualquier momento. El uso continuado del sitio implica la aceptación de los cambios.'
    }]
  }
};
type LegalModalProps = {
  doc: LegalDoc | null;
  onClose: () => void;
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (doc) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  return createPortal(<AnimatePresence data-id="element-121">
      {doc && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} aria-modal="true" role="dialog" aria-labelledby="legal-modal-title" data-id="element-122">
          <div className="absolute inset-0 bg-brand-navy/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-123" />

          <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.2
      }} data-id="element-124">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-125">
              <h2 id="legal-modal-title" className="text-xl font-bold text-brand-navy" data-id="element-126">
                {content[doc].title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="text-gray-400 hover:text-brand-navy transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green" data-id="element-127">
                <X className="w-6 h-6" data-id="element-128" />
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto space-y-5" data-id="element-129">
              {content[doc].sections.map(section => <div key={section.heading} data-id="element-130">
                  <h3 className="font-semibold text-brand-navy mb-1.5" data-id="element-131">
                    {section.heading}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-132">
                    {section.body}
                  </p>
                </div>)}
              <p className="text-xs text-gray-400 pt-2 border-t border-gray-100" data-id="element-133">
                Última actualización: {new Date().getFullYear()}. Este documento
                es un resumen general y no sustituye asesoría legal profesional.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}